import { useEffect, useRef } from "react";

/**
 * useShortcuts — central keyboard shortcut registry.
 *
 * Auto-disables on touch-only devices (no keyboard). Ignores key events when user is
 * typing in inputs/textareas/contentEditable.
 *
 * Keys:
 *   - single char: "e", "/", "?", "j", "k"
 *   - modified:    "mod+k" (Cmd on Mac, Ctrl on Win/Linux), "shift+c", "mod+enter"
 *   - special:     "enter", "escape", "arrowup", "arrowdown"
 *
 * Usage:
 *   useShortcuts([
 *     { keys: "j",        handler: () => moveDown(),  description: "Next row" },
 *     { keys: "k",        handler: () => moveUp(),    description: "Previous row" },
 *     { keys: "e",        handler: () => edit(),      description: "Edit selected" },
 *     { keys: "mod+k",    handler: () => openSearch(), description: "Open search" },
 *     { keys: "?",        handler: () => toggleHelp(), description: "Show shortcuts" },
 *   ], { enabled: !editing });
 */

// Shared registry so the help overlay can enumerate everything bound right now.
const registry = new Set();
const subscribers = new Set();

export function getAllShortcuts() {
  return Array.from(registry);
}

export function subscribeShortcuts(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function notify() {
  subscribers.forEach((fn) => {
    try { fn(getAllShortcuts()); } catch (_) { /* ignore subscriber errors */ }
  });
}

function isTypingContext(target) {
  if (!target) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

function matchesKey(event, keys) {
  const k = keys.toLowerCase();
  const parts = k.split("+").map((s) => s.trim());
  const target = parts[parts.length - 1];
  const needMod = parts.includes("mod");
  const needShift = parts.includes("shift");
  const needAlt = parts.includes("alt");

  const hasMod = event.metaKey || event.ctrlKey;
  if (needMod !== hasMod) return false;
  if (needShift !== event.shiftKey) return false;
  if (needAlt !== event.altKey) return false;

  const ek = event.key.toLowerCase();
  // Normalize a few aliases
  const aliases = {
    esc: "escape",
    return: "enter",
    space: " ",
  };
  const want = aliases[target] || target;
  if (want === "?") return ek === "?" || (event.shiftKey && ek === "/");
  return ek === want;
}

export default function useShortcuts(shortcuts, options = {}) {
  const { enabled = true } = options;
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    if (!enabled) return;
    // No-op on touch-only devices
    if (typeof window !== "undefined") {
      const isTouchOnly = window.matchMedia && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
      if (isTouchOnly) return;
    }

    // Register for help overlay enumeration
    const entries = shortcutsRef.current || [];
    entries.forEach((s) => registry.add(s));
    notify();

    const handler = (event) => {
      if (isTypingContext(event.target)) {
        // Allow Escape to still blur and Cmd+Enter to submit inside inputs
        if (event.key !== "Escape" && !(event.key === "Enter" && (event.metaKey || event.ctrlKey))) {
          return;
        }
      }
      for (const s of shortcutsRef.current || []) {
        if (matchesKey(event, s.keys)) {
          event.preventDefault();
          try { s.handler(event); } catch (err) { /* swallow per-handler errors */ console.error(err); }
          break;
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      entries.forEach((s) => registry.delete(s));
      notify();
    };
  }, [enabled]);
}
